import { useState, useEffect, useRef } from 'react';
import type { VehicleInput, VehicleTypeValue, VocationProfile } from '../types';
import { defaultOperatingCostInputs, defaultDepreciationInputs } from '../types';
import { getMarketRates, getGasPrices } from '../utils/api';
import { useScenarioStore } from '../store/useScenarioStore';
import { useAppModeStore } from '../store/useAppModeStore';
import VehicleSelector, { VEHICLE_CATEGORIES, formatTrimDisplay, type VehicleSelectorValue, type EPAVehicleDetail } from './VehicleSelector';

// ── Phase 21A: Vocation profile metadata ──────────────────────────────────────
// Per-option tooltips sourced from NREL Fleet DNA, U.S. Department of Energy.
const VOCATION_OPTIONS: { value: VocationProfile; label: string; description: string }[] = [
  {
    value: 'local_delivery',
    label: 'Local Delivery',
    description:
      '75% city / 25% highway. High idle time (15-25%). Frequent stops, low average speed (12-18 mph). Typical for parcel delivery, last-mile logistics, plumber/electrician service vans.',
  },
  {
    value: 'regional_haul',
    label: 'Regional Haul',
    description:
      '40% city / 60% highway. Moderate idle. Mix of urban delivery and interstate. Typical for food distribution, beverage delivery, regional freight.',
  },
  {
    value: 'long_haul',
    label: 'Long Haul',
    description:
      '10% city / 90% highway. Steady-state cruise dominant. Lowest idle percentage but highest overnight sleeper idle hours. Typical for over-the-road trucking, interstate freight.',
  },
  {
    value: 'urban_transit',
    label: 'Urban Transit',
    description:
      '90% city / 10% highway. Highest idle (20-35%). Many stops per mile. Typical for transit buses, shuttles.',
  },
  {
    value: 'vocational_heavy',
    label: 'Vocational / Heavy',
    description:
      '70% city / 30% highway. Very high idle (25-40%) for hydraulic PTO operation. Typical for refuse haulers, utility bucket trucks, dump trucks, cement mixers.',
  },
  {
    value: 'construction',
    label: 'Construction',
    description:
      '50% city / 50% highway. Significant idle (20-30%) plus off-road portion not captured in MPG. Typical for contractor work trucks, site-supervisor pickups, equipment haulers.',
  },
  {
    value: 'passenger_transport',
    label: 'Passenger Transport',
    description:
      '80% city / 20% highway. Moderate idle. Concentrated route windows. Typical for school buses, employee shuttles, paratransit.',
  },
];

const VOCATION_CATEGORY_HINT =
  'Vocation determines how city vs highway driving blend into your effective MPG. A long-haul tractor spends 90% on highway at steady speed; a refuse hauler spends 70% in city stop-and-go with high idle time. Picking the wrong vocation can swing fuel cost estimates by 20-40%. Defaults come from NREL Fleet DNA real-world driving data.';
const VOCATION_CATEGORY_SOURCE = 'NREL Fleet DNA, U.S. Department of Energy';

const LABOR_HINT =
  'Enter the FULLY-LOADED hourly cost: base wage + payroll taxes + benefits + insurance + overhead allocation. If you only have base wage handy, multiply by 1.30-1.40 as a starting estimate. BLS Employer Costs for Employee Compensation typically shows benefits adding 30-35% on top of wages for transportation occupations. For a $25/hr base wage, fully-loaded is typically $32-$38/hr.';
const LABOR_SOURCE = 'U.S. Bureau of Labor Statistics, Employer Costs for Employee Compensation';

// Phase 22.1: info-icon hover/tap tooltip for collapsing field subtext.
// Tooltip body preserves prior subtext copy verbatim; SourceCitation may
// follow inside `source` prop to keep the italic last-line attribution
// pattern from earlier phases.
interface InfoIconProps {
  hint: string;
  source?: string;
}
function InfoIcon({ hint, source }: InfoIconProps) {
  return (
    <span className="relative inline-flex group align-middle ml-1.5" tabIndex={0}>
      <span
        aria-hidden
        className="inline-flex w-4 h-4 items-center justify-center rounded-full bg-slate-700 text-slate-300 text-[10px] font-bold cursor-help group-hover:bg-sky-700 group-focus:bg-sky-700"
      >
        i
      </span>
      <span
        role="tooltip"
        className="invisible group-hover:visible group-focus:visible opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity absolute left-1/2 -translate-x-1/2 top-full mt-2 w-72 z-30 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs text-slate-200 shadow-lg leading-relaxed pointer-events-none"
      >
        {hint}
        {source && (
          <span className="block text-[11px] italic text-slate-400 opacity-80 mt-1.5">
            Source: {source}
          </span>
        )}
      </span>
    </span>
  );
}

// ── Reusable form primitives ──────────────────────────────────────────────────

interface FieldProps {
  label: string;
  hint?: string;
  infoTooltip?: { hint: string; source?: string };
  children: React.ReactNode;
}

function Field({ label, hint, infoTooltip, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-slate-300 flex items-center">
        <span>{label}</span>
        {infoTooltip && <InfoIcon hint={infoTooltip.hint} source={infoTooltip.source} />}
      </label>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
      {children}
    </div>
  );
}

const inputCls =
  'bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 w-full placeholder:text-slate-500';

const selectCls =
  'bg-slate-800 border border-slate-700 text-slate-100 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-sky-500 w-full';

// ── Item 1 & 2: Numeric input with string-state (clears correctly) + formatting

type NumericFormat = 'currency' | 'decimal1' | 'decimal2';

function formatDisplay(value: number | null, format?: NumericFormat, suffix?: string): string {
  if (value === null) return '';
  let str: string;
  if (format === 'currency') {
    str = value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  } else if (format === 'decimal2') {
    str = value.toFixed(2);
  } else if (format === 'decimal1') {
    str = value.toFixed(1);
  } else {
    str = String(value);
  }
  return suffix ? str + suffix : str;
}

interface NumericFieldProps {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  step?: number;
  min?: number;
  max?: number;
  hint?: string;
  infoTooltip?: { hint: string; source?: string };
  placeholder?: string;
  nullable?: boolean;
  prefix?: string;
  suffix?: string;
  format?: NumericFormat;
  defaultValue?: number;
  disabled?: boolean;
}

function NumericField({
  label,
  value,
  onChange,
  min,
  max,
  hint,
  infoTooltip,
  placeholder,
  nullable = false,
  prefix,
  suffix,
  format,
  defaultValue = 0,
  disabled = false,
}: NumericFieldProps) {
  // rawStr is null when not focused (display formatted), string when focused (raw editing)
  const [rawStr, setRawStr] = useState<string | null>(null);
  const isFocused = rawStr !== null;

  const displayValue = isFocused ? rawStr! : formatDisplay(value, format, suffix);

  function handleFocus() {
    // Show raw number without formatting so user can type freely
    setRawStr(value !== null ? String(value) : '');
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setRawStr(e.target.value);
  }

  function handleBlur() {
    const str = (rawStr ?? '').replace(/,/g, '').replace(/%$/, '').trim();
    const parsed = parseFloat(str);

    if (str === '' || isNaN(parsed)) {
      onChange(nullable ? null : defaultValue);
    } else {
      let clamped = parsed;
      if (min !== undefined) clamped = Math.max(min, clamped);
      if (max !== undefined) clamped = Math.min(max, clamped);
      onChange(clamped);
    }
    setRawStr(null);
  }

  const cls = inputCls + (prefix ? ' pl-7' : '') + (disabled ? ' opacity-50 cursor-not-allowed' : '');

  const inp = (
    <input
      type="text"
      inputMode="decimal"
      className={cls}
      value={displayValue}
      placeholder={isFocused ? '' : (placeholder ?? (nullable ? 'Optional' : ''))}
      onFocus={disabled ? undefined : handleFocus}
      onChange={disabled ? undefined : handleChange}
      onBlur={disabled ? undefined : handleBlur}
      disabled={disabled}
    />
  );

  return (
    <Field label={label} hint={hint} infoTooltip={infoTooltip}>
      {prefix ? (
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">
            {prefix}
          </span>
          {inp}
        </div>
      ) : (
        inp
      )}
    </Field>
  );
}

interface ToggleProps {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}

function Toggle({ label, value, onChange, hint }: ToggleProps) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`mt-0.5 relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none ${
          value ? 'bg-sky-500' : 'bg-slate-600'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
            value ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
      <div>
        <p className="text-sm font-medium text-slate-300">{label}</p>
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
      </div>
    </div>
  );
}

// ── Tab panels ────────────────────────────────────────────────────────────────

interface TabPanelProps {
  vehicle: VehicleInput;
  onChange: (v: VehicleInput) => void;
  horizonMonths?: number;
  epaDetail?: EPAVehicleDetail | null;
  selectorMsrp?: number;
  isCustom?: boolean;
  geoGasPriceLabel?: string | null;
  // Phase 21D-A: surfaces dealer-quote MSRP path + missing-MPG label when
  // the selected nameplate has no MSRP_TABLE / mpg_table entry.
  hasMsrp?: boolean;
  hasMpgData?: boolean;
  // v1.44.9: the table MSRP is a labeled estimate (diesel HD-pickup row =
  // gas base + diesel-uplift seed). Surfaces an "estimated — verify" note.
  msrpEstimated?: boolean;
}

function PurchaseTab({ vehicle, onChange, horizonMonths = 60, selectorMsrp, isCustom, hasMsrp = true, msrpEstimated = false }: TabPanelProps) {
  const d = vehicle.depreciation;
  const updDep = (patch: Partial<typeof d>) =>
    onChange({ ...vehicle, depreciation: { ...d, ...patch } });

  // Compute depreciation floor detection from category curve
  const epaCategory = vehicleTypeToEpaCategory(vehicle.vehicle_type);
  const _cat = VEHICLE_CATEGORIES[epaCategory];
  const _currentYear = new Date().getFullYear();
  const _vehicleAge = vehicle.model_year ? Math.max(0, _currentYear - vehicle.model_year) : 0;
  let depreciationResult: { depreciation_floored: boolean; floor_year: number } | null = null;
  if (_cat && d.purchase_price > 0 && _cat.depreciation_curve) {
    const _curve = _cat.depreciation_curve;
    const _floorValue = d.purchase_price * _cat.residual_floor;
    const _years = Math.ceil(horizonMonths / 12);
    let _residual = d.purchase_price;
    for (let yr = 1; yr <= _years; yr++) {
      const curveIdx = Math.min(_vehicleAge + yr - 1, _curve.length - 1);
      _residual -= d.purchase_price * _curve[curveIdx];
      if (_residual <= _floorValue) {
        depreciationResult = { depreciation_floored: true, floor_year: yr };
        break;
      }
    }
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <NumericField
            label="Purchase Price ($)"
            value={d.purchase_price}
            onChange={(v) => updDep({ purchase_price: v ?? 0 })}
            min={0}
            hint={
              !isCustom && !hasMsrp
                ? 'Enter from your dealer quote — public MSRP not published'
                : !isCustom && msrpEstimated
                ? 'Estimated price — verify and override with your dealer quote'
                : 'Full MSRP or agreed price'
            }
            prefix="$"
            format="currency"
          />
          {isCustom && (
            <p className="text-xs text-amber-500/60 mt-1">Enter purchase price</p>
          )}
          {!isCustom && !hasMsrp && (!d.purchase_price || d.purchase_price === 0) && (
            <p className="text-xs mt-1" style={{ color: 'var(--tco-neg)' }}>
              Required — enter the MSRP from your dealer quote.
            </p>
          )}
          {!isCustom && !hasMsrp && d.purchase_price > 0 && (
            <p className="text-xs mt-1" style={{ color: 'var(--tco-ink-3)' }}>
              Dealer-quote MSRP — no published list price for this configuration.
            </p>
          )}
          {!isCustom && hasMsrp && msrpEstimated && (
            <p className="text-xs mt-1" style={{ color: 'var(--tco-warn, #d97706)' }}>
              Estimated MSRP — gas base price plus an approximate diesel-engine
              uplift. Not a confirmed price; replace with your dealer quote.
            </p>
          )}
          {!isCustom && hasMsrp && !msrpEstimated && selectorMsrp != null && selectorMsrp > 0 && (
            <p className="text-xs text-slate-500 mt-1">
              2026 pricing data — verify current price before purchase.
            </p>
          )}
        </div>
        <div className="sm:col-span-2">
          <NumericField
            label="Residual Value at Horizon ($)"
            value={d.residual_value_year_5}
            onChange={(v) => updDep({ residual_value_year_5: v ?? 0 })}
            min={0}
            hint="Estimated trade-in or sale price at the end of your ownership horizon"
            prefix="$"
            format="currency"
          />
          {isCustom && <p className="text-xs text-amber-500/60 mt-0.5">Enter value</p>}
          {depreciationResult?.depreciation_floored && (
            <p className="text-xs text-amber-600 mt-1">
              Residual value estimate has reached the floor for this vehicle category
              at year {depreciationResult.floor_year}. At this age, mileage and
              condition are the primary remaining value drivers.
            </p>
          )}
        </div>
      </div>

    </div>
  );
}

function FinancingTab({ vehicle, onChange }: TabPanelProps) {
  const f = vehicle.financing;
  const d = vehicle.depreciation;
  const upd = (patch: Partial<typeof f>) =>
    onChange({ ...vehicle, financing: { ...f, ...patch } });

  // Read scenario-level sales_tax_rate for tax-inclusive cash calculation
  const salesTaxRate = useScenarioStore((s) => s.currentScenario.sales_tax_rate) ?? 0.08;

  const [rateData, setRateData] = useState<{
    available: boolean;
    newRate: number | null;
    usedRate: number | null;
  }>({ available: false, newRate: null, usedRate: null });
  const [isCashPurchase, setIsCashPurchase] = useState(false);
  const [savedLoanAmount, setSavedLoanAmount] = useState<number>(f.loan_amount);

  useEffect(() => {
    getMarketRates()
      .then((data) => {
        if (data?.rate_available && data.new_car_rate != null) {
          setRateData({ available: true, newRate: data.new_car_rate, usedRate: data.used_car_rate });
        } else {
          setRateData({ available: false, newRate: null, usedRate: null });
        }
      })
      .catch(() => {
        setRateData({ available: false, newRate: null, usedRate: null });
      });
  }, []);

  // Tax-inclusive cash required: purchase_price × (1 + tax) − loan_amount
  const taxInclusivePrice = d.purchase_price * (1 + salesTaxRate);
  const loanForCash = isCashPurchase ? 0 : f.loan_amount;
  const cashRequired = taxInclusivePrice - loanForCash;
  const cashNegative = !isCashPurchase && cashRequired < 0 && f.loan_amount > 0;

  // Rate label based on purchase condition
  const rateLabel = rateData.available
    ? (() => {
        const isNew = vehicle.purchase_condition === 'new';
        const rate = isNew ? rateData.newRate! : rateData.usedRate!;
        const lo = (rate - 2.0).toFixed(1);
        const hi = (rate - 1.0).toFixed(1);
        const prefix = isNew
          ? `Fed avg: ${rate.toFixed(1)}%`
          : `Fed avg: ${rate.toFixed(1)}% (est.)`;
        return `${prefix} — excellent credit typically ${lo}–${hi}%`;
      })()
    : 'Live rate unavailable — check bankrate.com for current rates';

  function handleCashToggle(v: boolean) {
    if (v) {
      setSavedLoanAmount(f.loan_amount);
      upd({ loan_amount: 0 });
    } else {
      upd({ loan_amount: savedLoanAmount });
    }
    setIsCashPurchase(v);
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      <div className="sm:col-span-2">
        <NumericField
          label="Loan Amount ($)"
          value={f.loan_amount}
          onChange={(v) => upd({ loan_amount: v ?? 0 })}
          min={0}
          hint="Amount you are financing. Set to 0 for a cash purchase."
          prefix="$"
          format="currency"
          disabled={isCashPurchase}
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-slate-400">
            Est. cash required (incl. tax):{' '}
            <span className={`font-medium ${cashNegative ? 'text-amber-400' : 'text-slate-200'}`}>
              ${Math.abs(cashRequired).toLocaleString('en-US', { maximumFractionDigits: 0 })}
              {cashNegative ? ' (over)' : ''}
            </span>
          </span>
          <button
            type="button"
            onClick={() => handleCashToggle(!isCashPurchase)}
            className={`flex items-center gap-2 text-xs font-medium transition-colors ${isCashPurchase ? 'text-sky-400' : 'text-slate-400 hover:text-slate-200'}`}
          >
            <span
              className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors ${isCashPurchase ? 'bg-sky-500' : 'bg-slate-600'}`}
            >
              <span
                className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${isCashPurchase ? 'translate-x-4' : 'translate-x-0'}`}
              />
            </span>
            Cash Purchase
          </button>
        </div>
        {cashNegative && (
          <p className="mt-1.5 text-xs text-amber-400 flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            Loan amount exceeds estimated purchase price plus tax.
          </p>
        )}
      </div>
      <NumericField
        label="Loan Term (months)"
        value={f.loan_term_months}
        onChange={(v) => upd({ loan_term_months: v ?? 60 })}
        min={12}
        max={96}
        defaultValue={60}
      />
      <div>
        <NumericField
          label="Annual Interest Rate"
          value={Math.round(f.annual_interest_rate * 10000) / 100}
          onChange={(v) => upd({ annual_interest_rate: (v ?? 0) / 100 })}
          min={0}
          max={30}
          hint="As a percentage — e.g. 6.5 for 6.5%"
          format="decimal1"
          suffix="%"
        />
        <p className={`mt-1.5 text-xs ${rateData.available ? 'text-slate-400' : 'text-slate-500'}`}>
          {rateLabel}
        </p>
      </div>
    </div>
  );
}

// Phase 26.5.2: client-side mirror of warranty_data.get_warranty_key() defaults
// so editor placeholders show the right manufacturer numbers without an API round-trip.
function defaultWarrantyForVehicle(label: string | undefined): {
  btbYears: number; btbMiles: number; ptYears: number; ptMiles: number;
  isRam: boolean;
} {
  const l = (label || '').toLowerCase();
  const isRam = l.includes('ram') || l.includes('promaster') || l.includes('dodge');
  if (l.includes('isuzu')) return { btbYears: 3, btbMiles: 75000, ptYears: 5, ptMiles: 75000, isRam: false };
  if (l.includes('hino')) return { btbYears: 2, btbMiles: 50000, ptYears: 5, ptMiles: 250000, isRam: false };
  if (l.includes('freightliner')) return { btbYears: 2, btbMiles: 999999, ptYears: 2, ptMiles: 999999, isRam: false };
  if (l.includes('mercedes') || l.includes('sprinter')) return { btbYears: 3, btbMiles: 36000, ptYears: 5, ptMiles: 100000, isRam: false };
  if (l.includes('ford')) {
    if (l.includes('f-650') || l.includes('f650') || l.includes('f-750') || l.includes('f750')) {
      return { btbYears: 2, btbMiles: 999999, ptYears: 5, ptMiles: 250000, isRam: false };
    }
    if (l.includes('e-transit') || l.includes('etransit') || l.includes('lightning')) {
      return { btbYears: 3, btbMiles: 36000, ptYears: 3, ptMiles: 36000, isRam: false };
    }
    if (l.includes('f-250') || l.includes('f-350') || l.includes('f-450') || l.includes('f-550') || l.includes('f-600')) {
      const diesel = l.includes('diesel');
      return { btbYears: 3, btbMiles: 36000, ptYears: 5, ptMiles: diesel ? 100000 : 60000, isRam: false };
    }
    return { btbYears: 3, btbMiles: 36000, ptYears: 3, ptMiles: 100000, isRam: false };
  }
  if (isRam) {
    if (l.includes('chassis') || l.includes('3500') || l.includes('4500') || l.includes('5500')) {
      const diesel = l.includes('diesel');
      return { btbYears: 3, btbMiles: 36000, ptYears: 5, ptMiles: diesel ? 100000 : 60000, isRam: true };
    }
    return { btbYears: 3, btbMiles: 36000, ptYears: 5, ptMiles: 60000, isRam: true };
  }
  if (l.includes('chevrolet') || l.includes('chevy') || l.includes('gmc') ||
      l.includes('silverado') || l.includes('sierra')) {
    return { btbYears: 3, btbMiles: 36000, ptYears: 5, ptMiles: 60000, isRam: false };
  }
  return { btbYears: 3, btbMiles: 36000, ptYears: 5, ptMiles: 60000, isRam: false };
}

function OperatingTab({ vehicle, onChange, isCustom, geoGasPriceLabel, hasMpgData = true }: TabPanelProps) {
  const o = vehicle.operating_costs;
  const upd = (patch: Partial<typeof o>) =>
    onChange({ ...vehicle, operating_costs: { ...o, ...patch } });
  // Phase 26.5.2: vehicle-level updater for new warranty / repair / custom fields.
  const updVeh = (patch: Partial<VehicleInput>) =>
    onChange({ ...vehicle, ...patch });
  const warrantyDefaults = defaultWarrantyForVehicle(vehicle.label);

  // EV type lock: if vehicle_type is ev_mainstream or ev_luxury_performance, force electric
  const isEvType = vehicle.vehicle_type === 'ev_mainstream' || vehicle.vehicle_type === 'ev_luxury_performance';
  const isEV = isEvType || o.fuel_type === 'electric';
  const isStaggered = o.is_staggered_tires;

  // Phase 22.7: scenario-level fuel-price defaults. The per-vehicle field
  // remains the override; when blank/zero the calc engine falls through to
  // the scenario default (then to EIA). Read-only here so the editor can
  // surface a "Using scenario default" hint.
  const scenarioGasPrice = useScenarioStore(
    (s) => s.currentScenario.scenario_fuel_cost_per_gallon,
  );
  const scenarioDieselPrice = useScenarioStore(
    (s) => s.currentScenario.scenario_diesel_price_per_gallon,
  );
  const scenarioKwhRate = useScenarioStore(
    (s) => s.currentScenario.scenario_electricity_rate_per_kwh,
  );
  const scenarioFuelPriceForVehicle =
    o.fuel_type === 'diesel' ? scenarioDieselPrice : scenarioGasPrice;
  const usingScenarioGasDefault =
    !isEV &&
    (o.fuel_cost_per_gallon == null || o.fuel_cost_per_gallon === 0) &&
    scenarioFuelPriceForVehicle != null &&
    scenarioFuelPriceForVehicle > 0;
  const usingScenarioElectricityDefault =
    isEV &&
    (o.electricity_cost_per_kwh == null || o.electricity_cost_per_kwh === 0) &&
    scenarioKwhRate != null &&
    scenarioKwhRate > 0;

  // Auto-set fuel type to electric when EV vehicle type is selected
  if (isEvType && o.fuel_type !== 'electric') {
    upd({ fuel_type: 'electric' });
  }

  // $/kWh: stored in dollars, displayed in cents
  const kwh_cents = o.electricity_cost_per_kwh != null
    ? Math.round(o.electricity_cost_per_kwh * 10000) / 100
    : null;

  // ── Item 7: tire interval in years (convert months ↔ years) ─────────────────
  const tireIntervalYears = o.tire_interval_months != null ? Math.round(o.tire_interval_months / 12) : null;
  const frontIntervalYears = o.front_tire_interval_months != null ? Math.round(o.front_tire_interval_months / 12) : null;
  const rearIntervalYears = o.rear_tire_interval_months != null ? Math.round(o.rear_tire_interval_months / 12) : null;

  // Tire cost warning: interval set but cost is 0
  const tireIntervalSet = tireIntervalYears != null && tireIntervalYears > 0;
  const tireCostMissing = tireIntervalSet && !o.tire_set_cost;
  const frontIntervalSet = frontIntervalYears != null && frontIntervalYears > 0;
  const frontCostMissing = frontIntervalSet && !o.front_tire_set_cost;
  const rearIntervalSet = rearIntervalYears != null && rearIntervalYears > 0;
  const rearCostMissing = rearIntervalSet && !o.rear_tire_set_cost;

  // ── Item 9: Winter tire fields ──────────────────────────────────────────────
  const hasWinterTires = o.has_winter_tires ?? false;
  const winterIntervalYears = o.winter_tire_interval_months != null
    ? Math.round(o.winter_tire_interval_months / 12)
    : null;

  return (
    <div className="space-y-6">
      {/* Insurance & Fees — first because insurance requires user input */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Insurance & Fees</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <NumericField
              label="Annual Insurance Premium ($)"
              value={o.annual_insurance_premium || null}
              onChange={(v) => upd({ annual_insurance_premium: v ?? 0 })}
              min={0}
              nullable
              prefix="$"
              format="currency"
              placeholder="Enter your annual insurance cost"
            />
            {(!o.annual_insurance_premium || o.annual_insurance_premium === 0) && (
              <p className="text-xs text-amber-400 mt-1">Required — enter your annual insurance cost</p>
            )}
          </div>
          <div>
            <NumericField
              label="Annual Registration Fee ($)"
              value={o.annual_registration_fee}
              onChange={(v) => upd({ annual_registration_fee: v ?? 0 })}
              min={0}
              prefix="$"
              format="currency"
            />
            <p className="text-xs text-slate-600 mt-1">
              Registration fees for formula-based states use official state formulas with representative local rate assumptions.
            </p>
          </div>
          <NumericField
            label="Annual Parking Cost ($)"
            value={o.annual_parking_cost}
            onChange={(v) => upd({ annual_parking_cost: v ?? 0 })}
            min={0}
            prefix="$"
            format="currency"
          />
          <NumericField
            label="Annual Tolls Cost ($)"
            value={o.annual_tolls_cost}
            onChange={(v) => upd({ annual_tolls_cost: v ?? 0 })}
            min={0}
            prefix="$"
            format="currency"
          />
        </div>
      </div>

      {/* Fuel */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Fuel / Energy</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Fuel Type">
            {isEvType ? (
              <div className="flex items-center gap-2">
                <div className={selectCls + ' opacity-60 cursor-not-allowed bg-slate-900'}>
                  Electric
                </div>
              </div>
            ) : (
              <select
                className={selectCls}
                value={o.fuel_type}
                onChange={(e) =>
                  upd({ fuel_type: e.target.value as 'gasoline' | 'diesel' | 'electric' })
                }
              >
                <option value="gasoline">Gasoline</option>
                <option value="diesel">Diesel</option>
                <option value="electric">Electric</option>
              </select>
            )}
            {isEvType && (
              <p className="text-xs text-sky-400 mt-1">EV selected — fuel type set to electric</p>
            )}
          </Field>
          {!isEV && (
            <>
              <div>
                <NumericField
                  label="Fuel Cost per Gallon ($)"
                  value={o.fuel_cost_per_gallon}
                  onChange={(v) => upd({ fuel_cost_per_gallon: v })}
                  min={0}
                  nullable
                  prefix="$"
                  format="decimal2"
                  placeholder={
                    usingScenarioGasDefault && scenarioFuelPriceForVehicle != null
                      ? `Scenario default $${scenarioFuelPriceForVehicle.toFixed(2)}`
                      : undefined
                  }
                />
                {/* Phase 22.7: per-vehicle override hint when scenario default is in effect */}
                {usingScenarioGasDefault && scenarioFuelPriceForVehicle != null && (
                  <p className="text-xs text-sky-400/70 mt-1">
                    Using scenario default ${scenarioFuelPriceForVehicle.toFixed(2)}/gal · Override here if needed
                  </p>
                )}
                {o.fuel_cost_per_gallon != null && o.fuel_cost_per_gallon > 0 && geoGasPriceLabel && geoGasPriceLabel !== 'fallback' && (
                  <p className="text-xs text-sky-400/70 mt-1">
                    National 52-week EIA average — verify for your area
                  </p>
                )}
                {o.fuel_cost_per_gallon != null && o.fuel_cost_per_gallon > 0 && geoGasPriceLabel === 'fallback' && (
                  <p className="text-xs text-amber-400 mt-1">
                    Estimated — server starting up, refresh to update
                  </p>
                )}
                {(o.fuel_cost_per_gallon == null || o.fuel_cost_per_gallon === 0) && !geoGasPriceLabel && !usingScenarioGasDefault && (
                  <p className="text-xs text-slate-500 mt-1 animate-pulse">
                    Select a state to auto-populate gas price from EIA data
                  </p>
                )}
                {o.requires_premium && (
                  <p className="text-xs text-amber-400 mt-1">
                    This vehicle requires premium fuel — premium price applied
                  </p>
                )}
              </div>
              <NumericField
                label="Fuel Efficiency (Combined MPG)"
                value={o.mpg}
                onChange={(v) => upd({ mpg: v })}
                min={0}
                nullable
              />
              {isCustom && <p className="text-xs text-amber-500/60 mt-0.5">Enter fuel economy</p>}
              {!isCustom && !hasMpgData && (!o.mpg || o.mpg === 0) && (
                <p className="text-xs mt-1" style={{ color: 'var(--tco-neg)' }}>
                  No EPA/manufacturer data available — enter from manufacturer specs or your fleet's actual consumption.
                </p>
              )}
              {!isCustom && !hasMpgData && o.mpg != null && o.mpg > 0 && (
                <p className="text-xs mt-1" style={{ color: 'var(--tco-ink-3)' }}>
                  Manual entry — no EPA/manufacturer combined MPG on file for this trim.
                </p>
              )}
            </>
          )}
          {isEV && (
            <>
              <NumericField
                label="Energy Consumption (Wh/mile) *"
                value={o.wh_per_mile}
                onChange={(v) => upd({ wh_per_mile: v })}
                min={100}
                max={600}
                hint="Check your vehicle's onboard efficiency display or EPA fuel economy label. For example, Tesla Model 3 LR is approximately 250 Wh/mile. Most EVs range from 200 to 400 Wh/mile."
                defaultValue={280}
              />
              {isCustom && <p className="text-xs text-amber-500/60 mt-0.5">Enter value</p>}
              <div>
                <NumericField
                  label="Electricity Cost (¢/kWh)"
                  value={kwh_cents}
                  onChange={(v) => upd({ electricity_cost_per_kwh: v != null ? v / 100 : null })}
                  min={0}
                  nullable
                  hint="Enter in cents, e.g. 13 for $0.13/kWh"
                  placeholder={
                    usingScenarioElectricityDefault && scenarioKwhRate != null
                      ? `Scenario default ${(scenarioKwhRate * 100).toFixed(1)}¢`
                      : undefined
                  }
                />
                {/* Phase 22.7: per-vehicle override hint when scenario default is in effect */}
                {usingScenarioElectricityDefault && scenarioKwhRate != null && (
                  <p className="text-xs text-sky-400/70 mt-1">
                    Using scenario default ${scenarioKwhRate.toFixed(3)}/kWh · Override here if needed
                  </p>
                )}
              </div>
              <NumericField
                label="EV Registration Surcharge ($/yr)"
                value={o.ev_registration_surcharge}
                onChange={(v) => upd({ ev_registration_surcharge: v })}
                min={0}
                nullable
                prefix="$"
                format="currency"
              />
            </>
          )}
        </div>
      </div>

      {/* Maintenance */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Maintenance</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <NumericField
            label="Annual Maintenance Cost ($)"
            value={o.annual_maintenance_cost}
            onChange={(v) => upd({ annual_maintenance_cost: v ?? 0 })}
            min={0}
            prefix="$"
            format="currency"
          />
        </div>
      </div>

      {/* Warranty & Repairs */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Warranty & Repairs</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <NumericField
            label="Warranty Remaining (months)"
            value={o.warranty_remaining_months}
            onChange={(v) => upd({ warranty_remaining_months: v ?? 0 })}
            min={0}
            hint="Months of factory or extended warranty remaining from today. Repair costs apply only after this period."
          />
          <NumericField
            label="Annual Repair Cost (Post-Warranty)"
            value={o.out_of_warranty_repair_annual}
            onChange={(v) => upd({ out_of_warranty_repair_annual: v ?? 0 })}
            min={0}
            hint="Unplanned repair costs after factory warranty expires"
            prefix="$"
            format="currency"
          />
        </div>
      </div>

      {/* Phase 26.5.2: Dynamic Warranty Coverage */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Warranty Coverage</p>
        <div className="space-y-4">
          <div>
            <p className="text-xs font-medium text-slate-300 mb-2">Bumper-to-bumper</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <NumericField
                label="Years"
                value={vehicle.btb_warranty_years ?? null}
                onChange={(v) => updVeh({ btb_warranty_years: v != null ? Math.round(v) : null })}
                min={0}
                nullable
                placeholder={String(warrantyDefaults.btbYears)}
                hint={`Default: ${warrantyDefaults.btbYears} years (per manufacturer). $0 repair costs while B2B is active.`}
              />
              <NumericField
                label="Miles"
                value={vehicle.btb_warranty_miles ?? null}
                onChange={(v) => updVeh({ btb_warranty_miles: v != null ? Math.round(v) : null })}
                min={0}
                nullable
                placeholder={warrantyDefaults.btbMiles >= 999999 ? 'Unlimited' : warrantyDefaults.btbMiles.toLocaleString('en-US')}
                format="currency"
                hint={`Default: ${warrantyDefaults.btbMiles >= 999999 ? 'unlimited' : warrantyDefaults.btbMiles.toLocaleString('en-US')} miles. Whichever comes first.`}
              />
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-300 mb-2">Powertrain</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <NumericField
                label="Years"
                value={vehicle.powertrain_warranty_years ?? null}
                onChange={(v) => updVeh({ powertrain_warranty_years: v != null ? Math.round(v) : null })}
                min={0}
                nullable
                placeholder={String(warrantyDefaults.ptYears)}
                hint={`Default: ${warrantyDefaults.ptYears} years. Covers engine, transmission, drivetrain. Partial repair costs (~65% covered) while powertrain is active but B2B has expired.`}
              />
              <NumericField
                label="Miles"
                value={vehicle.powertrain_warranty_miles ?? null}
                onChange={(v) => updVeh({ powertrain_warranty_miles: v != null ? Math.round(v) : null })}
                min={0}
                nullable
                placeholder={warrantyDefaults.ptMiles >= 999999 ? 'Unlimited' : warrantyDefaults.ptMiles.toLocaleString('en-US')}
                format="currency"
                hint={`Default: ${warrantyDefaults.ptMiles >= 999999 ? 'unlimited' : warrantyDefaults.ptMiles.toLocaleString('en-US')} miles.`}
              />
            </div>
          </div>
          {warrantyDefaults.isRam && (
            <p className="text-xs text-amber-400">
              ⚠ Ram's 10-year warranty applies to retail purchases only. Fleet purchases default to 5-year coverage.
            </p>
          )}
        </div>
      </div>

      {/* Phase 26.5.2: Repair Budget */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Repair Budget (post-warranty estimate)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <NumericField
            label="Annual Repair Cost"
            value={vehicle.annual_repair_cost ?? null}
            onChange={(v) => updVeh({ annual_repair_cost: v != null ? v : null })}
            min={0}
            nullable
            prefix="$"
            format="currency"
            placeholder="Class default"
            hint="Unscheduled repair costs after warranty expires. Automatically adjusted for warranty coverage above (Zone A: $0, Zone B: ~35%, Zone C: 100%). Source: NPTC fleet cost benchmarks."
          />
        </div>
      </div>

      {/* Phase 26.5.2: Custom Annual Cost */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Custom Annual Cost</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Label" hint="Free-text label that will appear in exports.">
            <input
              type="text"
              maxLength={80}
              className={inputCls}
              value={vehicle.custom_annual_cost_label ?? ''}
              onChange={(e) => updVeh({ custom_annual_cost_label: e.target.value.slice(0, 80) || null })}
              placeholder="e.g. Extended warranty, Fleet software"
            />
          </Field>
          <NumericField
            label="Annual Amount"
            value={vehicle.custom_annual_cost ?? null}
            onChange={(v) => updVeh({ custom_annual_cost: v != null ? v : null })}
            min={0}
            nullable
            prefix="$"
            format="currency"
            placeholder="0"
            hint="Flat annual cost not captured elsewhere. Applied every year of the ownership horizon."
          />
        </div>
      </div>

      {/* Tires */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Tires</p>
        <div className="space-y-4">
          <Toggle
            label="Staggered Tires"
            hint="Front and rear tires use different sizes or replacement intervals"
            value={isStaggered}
            onChange={(v) => upd({ is_staggered_tires: v })}
          />

          {isStaggered ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Item 7: Front interval as integer years */}
              <NumericField
                label="Front Tire Replacement Interval (years)"
                value={frontIntervalYears}
                onChange={(v) => upd({ front_tire_interval_months: v != null ? Math.round(v) * 12 : null })}
                min={1}
                nullable
                placeholder=""
                hint="e.g. 2 for every 2 years"
              />
              <div>
                <NumericField
                  label="Front Set Cost ($)"
                  value={o.front_tire_set_cost}
                  onChange={(v) => upd({ front_tire_set_cost: v })}
                  min={0}
                  nullable
                  placeholder=""
                  prefix="$"
                  format="currency"
                />
                {frontCostMissing && (
                  <p className="mt-1 text-xs text-amber-400">Enter tire set cost or set interval to 0 to exclude tire costs.</p>
                )}
              </div>
              {/* Item 7: Rear interval as integer years */}
              <NumericField
                label="Rear Tire Replacement Interval (years)"
                value={rearIntervalYears}
                onChange={(v) => upd({ rear_tire_interval_months: v != null ? Math.round(v) * 12 : null })}
                min={1}
                nullable
                placeholder=""
                hint="e.g. 2 for every 2 years"
              />
              <div>
                <NumericField
                  label="Rear Set Cost ($)"
                  value={o.rear_tire_set_cost}
                  onChange={(v) => upd({ rear_tire_set_cost: v })}
                  min={0}
                  nullable
                  placeholder=""
                  prefix="$"
                  format="currency"
                />
                {rearCostMissing && (
                  <p className="mt-1 text-xs text-amber-400">Enter tire set cost or set interval to 0 to exclude tire costs.</p>
                )}
              </div>
              <NumericField
                label="Mount and Balance per Event ($)"
                value={o.mount_balance_per_event}
                onChange={(v) => upd({ mount_balance_per_event: v ?? 0 })}
                min={0}
                prefix="$"
                format="currency"
              />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Item 7: Interval as integer years */}
              <NumericField
                label="Tire Replacement Interval (years)"
                value={tireIntervalYears}
                onChange={(v) => upd({ tire_interval_months: v != null ? Math.round(v) * 12 : null })}
                min={1}
                nullable
                hint="e.g. 2 for every 2 years"
              />
              <div>
                <NumericField
                  label="Full Set Cost ($)"
                  value={o.tire_set_cost}
                  onChange={(v) => upd({ tire_set_cost: v ?? 0 })}
                  min={0}
                  hint="Cost of a full set of 4 tires"
                  prefix="$"
                  format="currency"
                />
                {tireCostMissing && (
                  <p className="mt-1 text-xs text-amber-400">Enter tire set cost or set interval to 0 to exclude tire costs.</p>
                )}
              </div>
              <NumericField
                label="Mount and Balance per Event ($)"
                value={o.mount_balance_per_event}
                onChange={(v) => upd({ mount_balance_per_event: v ?? 0 })}
                min={0}
                prefix="$"
                format="currency"
              />
            </div>
          )}

          {/* Tire Age Override */}
          {(() => {
            const currentYear = new Date().getFullYear();
            const vehicleAgeMonths = vehicle.purchase_condition === 'used' && vehicle.model_year
              ? Math.max(0, (currentYear - vehicle.model_year) * 12)
              : 0;
            const tireInterval = o.tire_interval_months ?? 24;
            const autoCalcAge = tireInterval > 0 ? vehicleAgeMonths % tireInterval : 0;
            return (
              <div className="border-t border-slate-800 pt-4">
                <NumericField
                  label="Tire Age Override (months)"
                  value={o.tire_age_override != null ? o.tire_age_override : null}
                  onChange={(v) => upd({ tire_age_override: v != null && v >= 0 ? v : undefined })}
                  min={0}
                  nullable
                  hint={`Optional. Actual months of wear on current tires at purchase. If blank, estimated from vehicle age (currently calculated as ${autoCalcAge} months).`}
                  placeholder="Optional"
                />
              </div>
            );
          })()}

          {/* Item 9: Winter Tires Toggle */}
          <div className="border-t border-slate-800 pt-4">
            <Toggle
              label="Winter Tires"
              hint="Track seasonal tire costs separately from summer tires"
              value={hasWinterTires}
              onChange={(v) => upd({ has_winter_tires: v })}
            />
            {hasWinterTires && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <NumericField
                  label="Winter Tire Set Cost ($)"
                  value={o.winter_tire_set_cost ?? null}
                  onChange={(v) => upd({ winter_tire_set_cost: v ?? 0 })}
                  min={0}
                  prefix="$"
                  format="currency"
                  nullable
                  placeholder=""
                />
                <NumericField
                  label="Winter Tire Replacement Interval (years)"
                  value={winterIntervalYears}
                  onChange={(v) => upd({ winter_tire_interval_months: v != null ? Math.round(v) * 12 : 24 })}
                  min={1}
                  hint="e.g. 2 for every 2 years"
                  nullable
                  placeholder=""
                />
                <p className="sm:col-span-2 text-xs text-slate-500">
                  Winter driving percentage is set in Scenario Settings and applies to all vehicles.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

function ScoreSlider({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-slate-300">{label}</label>
        <span className="text-sm font-mono text-sky-400">{value} / 5</span>
      </div>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
      <input
        type="range"
        min={1}
        max={5}
        step={1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-sky-500 h-1.5 rounded cursor-pointer"
      />
      <div className="flex justify-between text-xs text-slate-600">
        <span>1</span>
        <span>2</span>
        <span>3</span>
        <span>4</span>
        <span>5</span>
      </div>
    </div>
  );
}

function OperationsTab({ vehicle, onChange, vocationMissing }: TabPanelProps & { vocationMissing?: boolean }) {
  // Phase 24: gate fleet-only behaviour on the nav-level activeMode rather
  // than the per-scenario mode in useScenarioStore.
  const appMode = useAppModeStore((s) => s.activeMode);
  // Phase 23: read scenario-level tax rate to gate the Section 179 toggle.
  // Hidden when marginal_tax_rate is 0 (tax-exempt entity) or purchase price
  // is unset.
  const scenarioMarginalTaxRate = useScenarioStore(
    (s) => s.currentScenario.marginal_tax_rate ?? 0.21,
  );
  // Phase 24.2: Section 179 election is a fleet-only concept; hide the
  // per-vehicle toggle in Personal mode regardless of the scenario tax rate
  // or purchase price.
  const showSec179Toggle =
    appMode === 'fleet' &&
    scenarioMarginalTaxRate > 0 &&
    (vehicle.depreciation.purchase_price ?? 0) > 0;

  const upd = (patch: Partial<VehicleInput>) => onChange({ ...vehicle, ...patch });

  const vocationMeta = VOCATION_OPTIONS.find((o) => o.value === vehicle.vocation_profile);

  // Helper text: computed total mileage at sale
  const miles = vehicle.annual_miles_per_vehicle && vehicle.annual_miles_per_vehicle > 0
    ? vehicle.annual_miles_per_vehicle
    : vehicle.depreciation.annual_mileage;
  const totalMiles = vehicle.ownership_period_years > 0 && miles > 0
    ? Math.round(vehicle.ownership_period_years * miles)
    : 0;

  // Phase 22.1: A5 — show charging infrastructure only for EV/PHEV powertrains.
  const fuel = (vehicle.operating_costs.fuel_type || '').toLowerCase();
  const showChargingInfra = fuel === 'electric' || fuel === 'phev';

  return (
    <div className="space-y-6">
      {/* Operating Profile */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Operating Profile</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <Field
              label="Vocation Profile"
              infoTooltip={{ hint: VOCATION_CATEGORY_HINT, source: VOCATION_CATEGORY_SOURCE }}
            >
              <select
                data-tour="vocation-selector"
                data-vocation-profile={vehicle.vehicle_id}
                className={
                  selectCls +
                  (vocationMissing ? ' border-red-500 ring-1 ring-red-500' : '')
                }
                value={vehicle.vocation_profile ?? ''}
                onChange={(e) =>
                  upd({ vocation_profile: (e.target.value || null) as VocationProfile | null })
                }
              >
                <option value="">— Select vocation —</option>
                {VOCATION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value} title={o.description}>
                    {o.label}
                  </option>
                ))}
              </select>
              {vocationMeta && (
                <p className="text-xs text-slate-400 mt-2">{vocationMeta.description}</p>
              )}
              {vocationMissing && (
                <p className="text-xs text-red-400 mt-1">
                  Select a vocation profile — required in fleet mode.
                </p>
              )}
            </Field>
          </div>

          <NumericField
            label="Operating Days per Week"
            value={vehicle.operating_days_per_week}
            onChange={(v) => upd({ operating_days_per_week: Math.round(v ?? 5) })}
            min={1}
            max={7}
            defaultValue={5}
            infoTooltip={{ hint: "Days per week this vehicle is in service. A delivery van running Mon-Fri = 5. A 24/7 transit bus or refuse hauler = 7. A weekend-only contractor truck = 2. This drives both labor cost and idle fuel calculations — getting it wrong silently skews TCO by 20-40% for vehicles with significant idle time." }}
          />

          <NumericField
            label="Weeks per Year"
            value={vehicle.weeks_per_year}
            onChange={(v) => upd({ weeks_per_year: v ?? 50 })}
            min={0}
            max={52}
            defaultValue={50}
            format="decimal1"
            infoTooltip={{ hint: "Default 50 weeks accounts for typical driver PTO/holidays. Adjust to 52 for 24/7 operations or lower for seasonal use." }}
          />

          {appMode === 'fleet' && (
            <div className="sm:col-span-2">
              <NumericField
                label="Annual Miles per Vehicle"
                value={vehicle.annual_miles_per_vehicle}
                onChange={(v) => upd({ annual_miles_per_vehicle: v != null ? Math.round(v) : null })}
                min={0}
                nullable
                format="currency"
                placeholder="Falls back to scenario annual miles"
                infoTooltip={{ hint: "Per-vehicle annual mileage in fleet mode. Leave blank to use the scenario-level annual miles. Each line item can override independently." }}
              />
            </div>
          )}

          <div className="sm:col-span-2">
            <NumericField
              label="Ownership Period (years)"
              value={vehicle.ownership_period_years}
              onChange={(v) => upd({ ownership_period_years: v ?? 5.0 })}
              min={1.0}
              max={15.0}
              defaultValue={5.0}
              format="decimal1"
              infoTooltip={{ hint: "Planned ownership period in years. Used as the TCO horizon for this vehicle. Fleet operators typically replace on 4-7 year cycles depending on vocation and warranty alignment." }}
            />
            {totalMiles > 0 && (
              <p className="text-xs text-slate-500 mt-1">
                At your annual mileage, this is approximately{' '}
                {totalMiles.toLocaleString('en-US')} total miles at sale.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Labor & Upfit */}
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Labor & Upfit</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div className="sm:col-span-2">
            <NumericField
              label="Loaded Hourly Labor Rate ($/hr)"
              value={vehicle.loaded_hourly_labor_rate || null}
              onChange={(v) => upd({ loaded_hourly_labor_rate: v ?? 0 })}
              min={0}
              nullable
              prefix="$"
              format="decimal2"
              infoTooltip={{ hint: LABOR_HINT, source: LABOR_SOURCE }}
            />
          </div>

          <NumericField
            label="Weekly Hours"
            value={vehicle.weekly_hours || null}
            onChange={(v) => upd({ weekly_hours: v ?? 0 })}
            min={0}
            nullable
            format="decimal1"
            infoTooltip={{ hint: "Total hours per week the vehicle is in service. Used with labor rate to compute annual labor cost." }}
          />

          <NumericField
            label="Drivers per Vehicle"
            value={vehicle.drivers_per_vehicle}
            onChange={(v) => upd({ drivers_per_vehicle: Math.round(v ?? 1) })}
            min={1}
            max={4}
            defaultValue={1}
            infoTooltip={{ hint: "For shift-work fleets where multiple drivers operate the same vehicle, set drivers per vehicle > 1. Multiplies labor cost proportionally." }}
          />

          <div className="sm:col-span-2">
            <NumericField
              label="Upfit Cost ($)"
              value={vehicle.upfit_cost || null}
              onChange={(v) => upd({ upfit_cost: v ?? 0 })}
              min={0}
              nullable
              prefix="$"
              format="currency"
              infoTooltip={{ hint: "If your vehicle has aftermarket upfitting (service body, ladder rack, lift gate, refrigeration, etc.), enter the upfit cost here. Leave at $0 if none. Upfit cost is added to the vehicle acquisition cost line in TCO." }}
            />
          </div>

          <div className="sm:col-span-2">
            <Field
              label="Upfit Description (optional)"
              infoTooltip={{ hint: "Optional — briefly describe the upfit (e.g., 'Knapheide service body + ladder rack')." }}
            >
              <input
                type="text"
                maxLength={200}
                className={inputCls}
                value={vehicle.upfit_description}
                onChange={(e) => upd({ upfit_description: e.target.value.slice(0, 200) })}
                placeholder="Optional"
              />
            </Field>
          </div>

          {showChargingInfra && (
            <NumericField
              label="Charging Infrastructure Cost ($)"
              value={vehicle.charging_infrastructure_cost || null}
              onChange={(v) => upd({ charging_infrastructure_cost: v ?? 0 })}
              min={0}
              nullable
              prefix="$"
              format="currency"
              infoTooltip={{ hint: "Optional one-time cost for EV charging setup (Level 2 charger installation, electrical work, etc.). Added to vehicle acquisition cost. Leave at $0 if charging at existing infrastructure." }}
            />
          )}

          <NumericField
            label="Purchase Incentives ($)"
            value={vehicle.purchase_incentives || null}
            onChange={(v) => upd({ purchase_incentives: v ?? 0 })}
            min={0}
            nullable
            prefix="$"
            format="currency"
            infoTooltip={{ hint: "Optional manual entry for federal/state tax credits or rebates that reduce effective purchase price. Subtracted from MSRP + upfit at the acquisition cost line. Verify eligibility independently; this tool does not validate credit qualification." }}
          />

          {showSec179Toggle && (
            <div className="pt-2" style={{ borderTop: '1px solid var(--tco-line)' }}>
              <Toggle
                label="Elect Section 179 for this vehicle"
                value={vehicle.sec179_elected ?? true}
                onChange={(v) => upd({ sec179_elected: v })}
                hint="On (default) — claim Section 179 first-year expensing. Off — skip Section 179 and use only bonus depreciation (e.g. when the annual §179 limit is already exhausted by other purchases)."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function EmotionalTab({ vehicle, onChange }: TabPanelProps) {
  const scores = vehicle.emotional.scores;
  const updScores = (patch: Partial<typeof scores>) =>
    onChange({ ...vehicle, emotional: { ...vehicle.emotional, scores: { ...scores, ...patch } } });

  return (
    <div className="space-y-6">
      <p className="text-slate-400 text-sm">
        Rate each dimension from 1 (low) to 5 (high). 3 is average. These scores inform the emotional overlay
        in the combined ranking.
      </p>
      <div className="space-y-5">
        <ScoreSlider
          label="Driving Enjoyment"
          hint="Score high if this vehicle is genuinely fun and engaging to drive; score low if it feels like a chore."
          value={scores.driving_enjoyment}
          onChange={(v) => updScores({ driving_enjoyment: v })}
        />
        <ScoreSlider
          label="Powertrain Character"
          hint="Score high for a distinctive, satisfying engine or motor; score low if the powertrain feels generic or uninspiring."
          value={scores.powertrain_character}
          onChange={(v) => updScores({ powertrain_character: v })}
        />
        <ScoreSlider
          label="Practical Utility"
          hint="Score high if this vehicle handles cargo, passengers, and daily tasks with ease; score low if it feels cramped or impractical."
          value={scores.practical_utility}
          onChange={(v) => updScores({ practical_utility: v })}
        />
        <ScoreSlider
          label="Winter Confidence"
          hint="Score high if AWD, traction systems, and ground clearance give you peace of mind in cold/snowy conditions; score low if you worry about winter capability."
          value={scores.winter_confidence}
          onChange={(v) => updScores({ winter_confidence: v })}
        />
        <ScoreSlider
          label="Brand Trust"
          hint="Score high if you trust this brand for long-term reliability and worry-free ownership; score low if you expect frequent issues or dealer problems."
          value={scores.brand_trust}
          onChange={(v) => updScores({ brand_trust: v })}
        />
        <ScoreSlider
          label="Status / Prestige"
          hint="Score high if this vehicle positively reflects on your social image or professional standing; score low if status is not a factor for you."
          value={scores.status_prestige}
          onChange={(v) => updScores({ status_prestige: v })}
        />
        <ScoreSlider
          label="Ownership Pride"
          hint="Score high if this vehicle aligns with your values and identity and you'd be proud to own it; score low if it feels like a compromise."
          value={scores.ownership_pride}
          onChange={(v) => updScores({ ownership_pride: v })}
        />
        <ScoreSlider
          label="Technology / Features"
          hint="Score high for a modern infotainment system, advanced driver aids, and connectivity; score low if the tech feels dated or frustrating."
          value={scores.technology_features}
          onChange={(v) => updScores({ technology_features: v })}
        />
      </div>
    </div>
  );
}


// ── Main VehicleEditor component ──────────────────────────────────────────────

type TabKey = 'purchase' | 'financing' | 'operating' | 'operations' | 'emotional';

// Phase 22.4 hard mode separation: Operations is fleet-only (vocation/idle/labor),
// Emotional is individual-only. Other three are shared.
const TABS: { key: TabKey; label: string; fleetHidden?: boolean; individualHidden?: boolean }[] = [
  { key: 'purchase', label: 'Purchase' },
  { key: 'financing', label: 'Financing' },
  { key: 'operating', label: 'Operating' },
  { key: 'operations', label: 'Operations', individualHidden: true },
  { key: 'emotional', label: 'Emotional', fleetHidden: true },
];

interface VehicleEditorProps {
  vehicle: VehicleInput;
  onSave: (vehicle: VehicleInput) => void;
  onClose: () => void;
  horizonMonths?: number;
  onEpaAutoFill?: () => void;
  geoGasPriceLabel?: string | null;
}

// ── EPA category ↔ VehicleTypeValue mapping ───────────────────────────────────

function epaCategoryToVehicleType(category: string): VehicleTypeValue | null {
  const map: Record<string, VehicleTypeValue> = {
    luxury_sedan: 'luxury_sedan',
    mainstream_sedan: 'mainstream_sedan',
    luxury_suv: 'luxury_suv',
    mainstream_suv: 'mainstream_suv',
    truck: 'pickup_truck',
    sports_car: 'performance_sports',
    ev_mainstream: 'ev_mainstream',
    ev_luxury: 'ev_luxury_performance',
    commercial_van: 'commercial_van',
  };
  return map[category] ?? null;
}

function vehicleTypeToEpaCategory(vt: VehicleTypeValue | null): string {
  if (!vt) return '';
  const map: Record<VehicleTypeValue, string> = {
    luxury_sedan: 'luxury_sedan',
    mainstream_sedan: 'mainstream_sedan',
    luxury_suv: 'luxury_suv',
    mainstream_suv: 'mainstream_suv',
    pickup_truck: 'truck',
    performance_sports: 'sports_car',
    ev_mainstream: 'ev_mainstream',
    ev_luxury_performance: 'ev_luxury',
    exotic_high_performance: 'sports_car',
    commercial_van: 'commercial_van',
  };
  return map[vt] ?? '';
}

const CURRENT_YEAR = new Date().getFullYear();

export default function VehicleEditor({ vehicle, onSave, onClose, horizonMonths = 60, onEpaAutoFill, geoGasPriceLabel }: VehicleEditorProps) {
  const [draft, setDraft] = useState<VehicleInput>({ ...vehicle });
  const [activeTab, setActiveTab] = useState<TabKey>('purchase');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [epaAutoFillVisible, setEpaAutoFillVisible] = useState(false);
  const updateVehicle = useScenarioStore((s) => s.updateVehicle);
  // Phase 22.4 hard mode separation: drop fleet-only tabs in individual mode and
  // individual-only tabs in fleet mode. Phase 24 promotes the gate from the
  // scenario-store coupling to the nav-level useAppModeStore so the nav
  // toggle is the single source of truth.
  const appMode = useAppModeStore((s) => s.activeMode);
  const visibleTabs = TABS.filter((t) => {
    if (t.fleetHidden && appMode === 'fleet') return false;
    if (t.individualHidden && appMode !== 'fleet') return false;
    return true;
  });

  // Bug 3 fix: sync geo-populated fields from store's vehicle prop to local draft
  useEffect(() => {
    setDraft((prev) => {
      const oc = prev.operating_costs;
      const voc = vehicle.operating_costs;
      const needsSync =
        (voc.fuel_cost_per_gallon && voc.fuel_cost_per_gallon !== oc.fuel_cost_per_gallon) ||
        (voc.electricity_cost_per_kwh && voc.electricity_cost_per_kwh !== oc.electricity_cost_per_kwh) ||
        (voc.annual_registration_fee && voc.annual_registration_fee !== oc.annual_registration_fee) ||
        (voc.ev_registration_surcharge && voc.ev_registration_surcharge !== oc.ev_registration_surcharge);
      if (!needsSync) return prev;
      return {
        ...prev,
        operating_costs: {
          ...oc,
          fuel_cost_per_gallon: voc.fuel_cost_per_gallon || oc.fuel_cost_per_gallon,
          electricity_cost_per_kwh: voc.electricity_cost_per_kwh || oc.electricity_cost_per_kwh,
          annual_registration_fee: voc.annual_registration_fee || oc.annual_registration_fee,
          ev_registration_surcharge: voc.ev_registration_surcharge || oc.ev_registration_surcharge,
        },
      };
    });
  }, [vehicle.operating_costs.fuel_cost_per_gallon, vehicle.operating_costs.electricity_cost_per_kwh, vehicle.operating_costs.annual_registration_fee, vehicle.operating_costs.ev_registration_surcharge]);

  // VehicleSelector state
  const [selectorValue, setSelectorValue] = useState<VehicleSelectorValue>({
    // Pre-demo round 2: coerce model_year to a number — legacy saved scenarios
    // may have stored it as a string, which would fail strict-equality lookup
    // against the supportedYears integer array and trip the stale "no data for
    // this year" warning.
    year: Number(vehicle.model_year) || new Date().getFullYear(),
    make: '',
    model: '',
    trim: '',
    msrp: vehicle.depreciation.purchase_price,
    vehicle_category: vehicleTypeToEpaCategory(vehicle.vehicle_type),
    category_was_auto_assigned: false,
    condition: vehicle.purchase_condition,
    label: vehicle.label,
    epa_vehicle_id: null,
    epa_detail: null,
    is_custom: false,
    warranty_months: 0,
    depreciation_override_5yr: null,
    msrp_fuel_type: null,
    // Phase 21D-A: defaults assume MSRP_TABLE coverage; overridden when
    // the user selects a catalog-only nameplate or a row with no mpg_table
    // entry. The reconstruction useEffect below also re-syncs these from
    // the /trims response when re-opening an existing vehicle.
    has_msrp: true,
    has_mpg_data: true,
    body_style: null,
    gvwr_class: null,
    msrp_estimated: false,
  });

  // Tracks whether the user has interacted with the selector (make/model/trim change).
  // If they have, the async reconstruction below must not overwrite their selection.
  const selectorTouchedRef = useRef(false);

  // Reconstruct make/model/trim from saved label when editing an existing vehicle.
  // The label format is "{year} {make} {model} {trimDisplay}".
  // We fetch API data to match each segment, then update selectorValue so the
  // dropdowns show the correct selections without triggering handleSelectorChange.
  useEffect(() => {
    if (!vehicle.label || vehicle.label === 'New Vehicle' || !vehicle.model_year) return;
    let cancelled = false;

    const apiBase = (import.meta.env.VITE_API_BASE_URL || '') + '/api';
    const yearStr = String(vehicle.model_year);
    let remaining = vehicle.label;
    if (remaining.startsWith(yearStr + ' ')) {
      remaining = remaining.slice(yearStr.length + 1);
    } else if (remaining === yearStr) {
      return;
    } else {
      return;
    }

    fetch(`${apiBase}/vehicles/makes`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled || selectorTouchedRef.current) return;
        const makes: string[] = data.makes ?? [];
        // Sort by length descending so "Land Rover" matches before "Land"
        const sortedMakes = [...makes].sort((a, b) => b.length - a.length);
        const matchedMake = sortedMakes.find((m) =>
          remaining.toLowerCase().startsWith(m.toLowerCase() + ' ') ||
          remaining.toLowerCase() === m.toLowerCase()
        );
        if (!matchedMake) return;
        const afterMake = remaining.slice(matchedMake.length).trim();

        return fetch(`${apiBase}/vehicles/models?make=${encodeURIComponent(matchedMake)}`)
          .then((r) => r.json())
          .then((mData) => {
            if (cancelled || selectorTouchedRef.current) return;
            const models: string[] = mData.models ?? [];
            const sortedModels = [...models].sort((a, b) => b.length - a.length);
            const matchedModel = sortedModels.find((m) =>
              afterMake.toLowerCase().startsWith(m.toLowerCase() + ' ') ||
              afterMake.toLowerCase() === m.toLowerCase()
            );
            if (!matchedModel) return;
            const afterModel = afterMake.slice(matchedModel.length).trim();

            return fetch(`${apiBase}/vehicles/trims?make=${encodeURIComponent(matchedMake)}&model=${encodeURIComponent(matchedModel)}`)
              .then((r) => r.json())
              .then((tData) => {
                if (cancelled || selectorTouchedRef.current) return;
                const vType: string = tData.vehicle_type ?? '';
                const wMonths: number = tData.warranty_months ?? 0;

                type TrimRow = {
                  trim: string;
                  msrp: number;
                  has_msrp?: boolean;
                  has_mpg_data?: boolean;
                  body_style?: string | null;
                  gvwr_class?: number | null;
                  msrp_estimated?: boolean;
                };
                const trimOptionsFull: TrimRow[] = (tData.trims ?? []) as TrimRow[];
                let matchedTrim = '';
                let matchedMsrp = vehicle.depreciation.purchase_price;
                let matchedTrimRow: TrimRow | undefined;
                if (afterModel) {
                  // Match display-formatted trim against remaining label
                  const trimMatch = trimOptionsFull.find((t) =>
                    formatTrimDisplay(t.trim).toLowerCase() === afterModel.toLowerCase()
                  ) || trimOptionsFull.find((t) =>
                    t.trim.toLowerCase() === afterModel.toLowerCase()
                  );
                  if (trimMatch) {
                    matchedTrim = trimMatch.trim || '(base)';
                    matchedMsrp = trimMatch.msrp || matchedMsrp;
                    matchedTrimRow = trimMatch;
                  }
                } else {
                  // No trim text in label — base trim
                  const baseTrim = trimOptionsFull.find((t) => t.trim === '');
                  if (baseTrim) {
                    matchedTrim = '(base)';
                    matchedMsrp = baseTrim.msrp || matchedMsrp;
                    matchedTrimRow = baseTrim;
                  }
                }

                const depOvr: number | null = tData.depreciation_override_5yr ?? null;

                setSelectorValue((prev) => ({
                  ...prev,
                  make: matchedMake,
                  model: matchedModel,
                  trim: matchedTrim,
                  msrp: matchedMsrp,
                  vehicle_category: vType || prev.vehicle_category,
                  category_was_auto_assigned: !!vType,
                  warranty_months: wMonths,
                  depreciation_override_5yr: depOvr,
                  has_msrp: matchedTrimRow?.has_msrp ?? true,
                  has_mpg_data: matchedTrimRow?.has_mpg_data ?? true,
                  body_style: matchedTrimRow?.body_style ?? null,
                  gvwr_class: matchedTrimRow?.gvwr_class ?? null,
                  msrp_estimated: matchedTrimRow?.msrp_estimated ?? false,
                }));
              });
          });
      })
      .catch(() => { /* Best-effort reconstruction */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track which category last had defaults applied — detect category switch
  const lastAppliedCategoryRef = useRef(vehicleTypeToEpaCategory(vehicle.vehicle_type));
  // Pre-demo round 2: track which MSRP value last drove a residual recompute.
  // The previous condition (`val.msrp > 0`) fired on every handleSelectorChange
  // call — including override-label keystrokes that don't actually change the
  // MSRP — and overwrote a user's manually-entered residual. Recompute only
  // when the MSRP value actually changes from the last applied value.
  const lastAppliedMsrpRef = useRef<number>(vehicle.depreciation.purchase_price);
  // Track latest operating_costs and depreciation across rapid handleSelectorChange calls
  // so the EPA callback doesn't overwrite handleTrim's changes with stale draft data.
  const latestOcRef = useRef(draft.operating_costs);
  const latestDepRef = useRef(draft.depreciation);

  function handleSelectorChange(val: VehicleSelectorValue) {
    selectorTouchedRef.current = true;
    setSelectorValue(val);
    const vehicleType = epaCategoryToVehicleType(val.vehicle_category);
    const defaultOc = defaultOperatingCostInputs();
    const defaultDep = defaultDepreciationInputs();

    let updatedDraft: VehicleInput = {
      ...draft,
      label: val.label,
      model_year: val.year,
      purchase_condition: val.condition,
      vehicle_type: vehicleType,
      // Use latest refs instead of stale draft closure for OC and depreciation
      operating_costs: latestOcRef.current,
      depreciation: latestDepRef.current,
    };

    // Auto-populate MSRP from table when trim is selected — always overwrite
    // so the purchase price field matches exactly what the table returns.
    if (val.msrp > 0) {
      updatedDraft.depreciation = { ...updatedDraft.depreciation, purchase_price: val.msrp };
    }

    // Phase 22.2 — propagate MSRP-table-sourced fuel_type as the canonical
    // pre-EPA value. Lets EV/PHEV-conditional UI (e.g. charging infrastructure
    // cost field) render immediately on trim select, without waiting for EPA
    // detail (which is missing for many 2026 commercial trims).
    if (val.msrp_fuel_type) {
      const raw = val.msrp_fuel_type.toLowerCase();
      let mapped: 'gasoline' | 'diesel' | 'electric' | 'phev' | null = null;
      if (raw === 'electric') mapped = 'electric';
      else if (raw === 'phev') mapped = 'phev';
      else if (raw === 'diesel') mapped = 'diesel';
      else if (raw === 'gasoline' || raw === 'hybrid') mapped = 'gasoline';
      if (mapped) {
        updatedDraft.operating_costs = {
          ...updatedDraft.operating_costs,
          fuel_type: mapped as typeof updatedDraft.operating_costs.fuel_type,
        };
      }
    }

    // Pre-populate category defaults. Force-refresh when category changes
    // (e.g., switching from luxury_sedan to truck) so stale values don't persist.
    const cat = VEHICLE_CATEGORIES[val.vehicle_category];
    if (cat) {
      const categoryChanged = val.vehicle_category !== lastAppliedCategoryRef.current;
      lastAppliedCategoryRef.current = val.vehicle_category;
      const oc = updatedDraft.operating_costs;
      const patchOc: Partial<typeof oc> = {};
      if (categoryChanged || oc.annual_maintenance_cost === defaultOc.annual_maintenance_cost || oc.annual_maintenance_cost === 0) {
        patchOc.annual_maintenance_cost = cat.annual_maintenance_base;
      }
      if (categoryChanged || oc.tire_interval_months === defaultOc.tire_interval_months || oc.tire_interval_months === null || oc.tire_interval_months === 0) {
        patchOc.tire_interval_months = cat.tire_interval_months;
      }
      if (categoryChanged || oc.tire_set_cost === defaultOc.tire_set_cost || oc.tire_set_cost === 0) {
        patchOc.tire_set_cost = cat.tire_set_cost;
      }
      if (categoryChanged || oc.out_of_warranty_repair_annual === defaultOc.out_of_warranty_repair_annual || oc.out_of_warranty_repair_annual === 0) {
        patchOc.out_of_warranty_repair_annual = cat.annual_repair_base;
      }
      // Warranty only auto-populates when warranty_months > 0 (set by VehicleSelector
      // only when year is in MSRP table, make is in WARRANTY_TABLE, and trim is selected).
      // For unsupported years or custom mode, warranty_months = 0 → field stays blank for manual entry.
      if (val.warranty_months > 0) {
        const currentYear = new Date().getFullYear();
        const vehicleAge = val.year ? Math.max(0, currentYear - val.year) : 0;
        patchOc.warranty_remaining_months = Math.max(0, val.warranty_months - (vehicleAge * 12));
      } else {
        patchOc.warranty_remaining_months = 0;
      }
      if (Object.keys(patchOc).length > 0) {
        updatedDraft.operating_costs = { ...oc, ...patchOc };
      }
      // Recalculate residual when MSRP value changes (trim select), category
      // changes, or the residual is still at the default. Pre-demo round 2:
      // gate on MSRP *value change*, not `msrp > 0` — the prior gate clobbered
      // user-entered residuals on every handleSelectorChange call (e.g. when
      // typing in the override-label field) because the MSRP value was already
      // populated and stayed > 0.
      const msrpChanged = val.msrp > 0 && val.msrp !== lastAppliedMsrpRef.current;
      if (val.msrp > 0) lastAppliedMsrpRef.current = val.msrp;
      const dep = updatedDraft.depreciation;
      if (dep.purchase_price > 0 && (categoryChanged || msrpChanged || dep.residual_value_year_5 === defaultDep.residual_value_year_5 || dep.residual_value_year_5 === 0)) {
        updatedDraft.depreciation = {
          ...dep,
          residual_value_year_5: (() => {
            const horizonYears = Math.max(1, Math.round(horizonMonths / 12));
            const currentYear = new Date().getFullYear();
            const vehicleAge = val.year ? Math.max(0, currentYear - val.year) : 0;
            // For used vehicles, start the curve from the vehicle's current age
            const curveStart = vehicleAge;
            const curveEnd = vehicleAge + horizonYears;
            const curve = cat.depreciation_curve;

            // Model-level depreciation override: scale the curve so the 5-year
            // total matches the iSeeCars study value while preserving curve shape
            let scaleFactor = 1.0;
            if (val.depreciation_override_5yr != null) {
              const category5yrSum = curve.slice(0, 5).reduce((a, b) => a + b, 0);
              if (category5yrSum > 0) {
                scaleFactor = val.depreciation_override_5yr / category5yrSum;
              }
            }

            let totalDepPct = 0;
            for (let yr = curveStart; yr < curveEnd; yr++) {
              const idx = Math.min(yr, curve.length - 1);
              totalDepPct += curve[idx] * scaleFactor;
            }
            const calculated = dep.purchase_price * Math.max(0, 1 - totalDepPct);
            const floor = dep.purchase_price * cat.residual_floor;
            return Math.round(Math.max(calculated, floor));
          })(),
        };
      }
    }

    // When EPA detail is cleared (model/make change), reset ALL EPA-derived fields
    // so stale values from the previous vehicle don't persist in the draft.
    if (!val.epa_detail && !val.epa_vehicle_id) {
      const oc = updatedDraft.operating_costs;
      const clearPatch: Partial<typeof oc> = {};
      if (oc.wh_per_mile && oc.wh_per_mile > 0) {
        clearPatch.wh_per_mile = 0;
        clearPatch.mpg = 0;
      }
      if (oc.requires_premium) {
        clearPatch.requires_premium = false;
      }
      // Reset fuel_type to gasoline so stale 'electric' from a previous EV doesn't persist
      if (oc.fuel_type !== 'gasoline') {
        clearPatch.fuel_type = 'gasoline';
      }
      if (Object.keys(clearPatch).length > 0) {
        updatedDraft.operating_costs = { ...oc, ...clearPatch };
      }
    }

    // EPA auto-fill when a trim is selected and epa_detail is available
    if (val.epa_detail) {
      const detail = val.epa_detail;
      const oc = updatedDraft.operating_costs;
      const dep = updatedDraft.depreciation;
      const patchOc: Partial<typeof oc> = {};
      const patchDep: Partial<typeof dep> = {};
      let anyFilled = false;

      // MPG: always update from EPA data when available
      if (detail.mpg_combined > 0) {
        patchOc.mpg = detail.mpg_combined;
        anyFilled = true;
      }

      // Wh/mile: fill if EV and EPA has a value
      if (detail.is_ev && detail.wh_per_mile > 0) {
        patchOc.wh_per_mile = detail.wh_per_mile;
        anyFilled = true;
      }

      // Fuel type: always update from EPA data
      const rawFuel = detail.fuel_type.toLowerCase();
      let mappedFuel: 'gasoline' | 'diesel' | 'electric' = 'gasoline';
      if (rawFuel.includes('electric')) mappedFuel = 'electric';
      else if (rawFuel.includes('diesel')) mappedFuel = 'diesel';
      patchOc.fuel_type = mappedFuel;
      anyFilled = true;

      // Premium fuel detection — explicitly set both true/false to clear stale state
      const isPremium = rawFuel.includes('premium');
      patchOc.requires_premium = isPremium;

      // Auto-apply premium gas price immediately (don't wait for geo defaults)
      if (isPremium) {
        getGasPrices().then((gp) => {
          if (gp?.premium) {
            setDraft(prev => ({
              ...prev,
              operating_costs: { ...prev.operating_costs, fuel_cost_per_gallon: gp.premium },
            }));
          }
        }).catch(() => {});
      }

      if (Object.keys(patchOc).length > 0) {
        updatedDraft.operating_costs = { ...updatedDraft.operating_costs, ...patchOc };
      }
      if (Object.keys(patchDep).length > 0) {
        updatedDraft.depreciation = { ...updatedDraft.depreciation, ...patchDep };
      }
      if (anyFilled) {
        // Dispatch directly to store keyed by vehicle ID to bypass stale-closure risk on draft state
        // Use updatedDraft (not stale vehicle prop) — updatedDraft already has patchOc/patchDep merged
        const payload: Partial<VehicleInput> = {
          ...(Object.keys(patchOc).length > 0 ? { operating_costs: updatedDraft.operating_costs } : {}),
          // Always push depreciation so MSRP is in store for geo defaults registration calc
          depreciation: updatedDraft.depreciation,
        };
        updateVehicle(vehicle.vehicle_id, payload);
        setEpaAutoFillVisible(true);
        setTimeout(() => setEpaAutoFillVisible(false), 4000);
        // Trigger geo defaults re-application now that vehicle has EPA data
        if (onEpaAutoFill) onEpaAutoFill();
      }
    }

    // Push MSRP to store immediately when trim is selected so geo defaults use correct value
    if (val.msrp > 0) {
      updateVehicle(vehicle.vehicle_id, { depreciation: updatedDraft.depreciation });
    }

    // Update refs so the next handleSelectorChange call (e.g. EPA callback) sees latest values
    latestOcRef.current = updatedDraft.operating_costs;
    latestDepRef.current = updatedDraft.depreciation;
    setDraft(updatedDraft);
  }

  // Phase 22.1 A7: vocation validation flag — drives red border + inline error in OperationsTab.
  const [vocationMissing, setVocationMissing] = useState(false);

  function handleSave() {
    const isEV =
      draft.vehicle_type === 'ev_mainstream' ||
      draft.vehicle_type === 'ev_luxury_performance' ||
      draft.operating_costs.fuel_type === 'electric';

    if (isEV && (!draft.operating_costs.wh_per_mile || draft.operating_costs.wh_per_mile <= 0)) {
      setSaveError('Energy Consumption (Wh/mile) is required for EVs. Switch to the Operating tab to fill it in.');
      setActiveTab('operating');
      return;
    }

    // Phase 21D-A: hard-stop save when a catalog-only nameplate has no
    // user-entered MSRP. Catalog rows have no public 2-source price; a $0
    // purchase_price would silently produce garbage TCO output.
    if (
      !selectorValue.is_custom &&
      selectorValue.has_msrp === false &&
      (!draft.depreciation.purchase_price || draft.depreciation.purchase_price <= 0)
    ) {
      setSaveError('Purchase price is required. Enter the MSRP from your dealer quote in the Purchase tab.');
      setActiveTab('purchase');
      return;
    }

    // Phase 21D-A: hard-stop save when a nameplate has no mpg_table row and
    // the user has not entered an MPG manually. EVs are excluded — wh_per_mile
    // covers their energy consumption (validated separately above).
    if (
      !selectorValue.is_custom &&
      selectorValue.has_mpg_data === false &&
      !isEV &&
      (!draft.operating_costs.mpg || draft.operating_costs.mpg <= 0)
    ) {
      setSaveError("Fuel economy is required. Enter from manufacturer specs or your fleet's actual consumption in the Operating tab.");
      setActiveTab('operating');
      return;
    }

    if (!draft.operating_costs.annual_insurance_premium || draft.operating_costs.annual_insurance_premium <= 0) {
      setSaveError('Annual insurance is required. Enter your cost in the Operating tab before saving.');
      setActiveTab('operating');
      return;
    }

    // Phase 22.1 A7: vocation profile required in fleet mode.
    if (appMode === 'fleet' && !draft.vocation_profile) {
      const lineItemName = draft.label || 'this vehicle';
      setSaveError(`Select a vocation profile for ${lineItemName}.`);
      setActiveTab('operations');
      setVocationMissing(true);
      return;
    }
    setVocationMissing(false);

    // Merge latest geo-populated values from store to avoid saving stale data
    // (geo defaults are async and may not have synced to draft yet)
    const storeVehicle = useScenarioStore.getState().currentScenario.vehicles.find(
      (v) => v.vehicle_id === vehicle.vehicle_id,
    );
    const savedDraft = { ...draft };

    // Pre-demo round 2: the "Residual Value at Horizon" editor field writes to
    // depreciation.residual_value_year_5 (used by compute_five_year_depreciation
    // to derive the implied annual retention rate). Phase 21H added a separate
    // expected_residual_value field that drives "Residual Value Reclaimed" in
    // the comparison table via residual_calc.calculate_residual_value_at_period_end.
    // Mirror the editor input into expected_residual_value so a single user
    // entry honors both calc paths (otherwise the comparison table silently
    // falls back to the Argonne curve, ignoring the user's number).
    if (savedDraft.depreciation.residual_value_year_5 > 0) {
      savedDraft.expected_residual_value = savedDraft.depreciation.residual_value_year_5;
      savedDraft.residual_data_source = 'user_override';
    } else {
      savedDraft.expected_residual_value = null;
      savedDraft.residual_data_source = null;
    }
    if (storeVehicle) {
      const storeOc = storeVehicle.operating_costs;
      savedDraft.operating_costs = {
        ...savedDraft.operating_costs,
        fuel_cost_per_gallon: storeOc.fuel_cost_per_gallon || savedDraft.operating_costs.fuel_cost_per_gallon,
        electricity_cost_per_kwh: storeOc.electricity_cost_per_kwh || savedDraft.operating_costs.electricity_cost_per_kwh,
        annual_registration_fee: storeOc.annual_registration_fee || savedDraft.operating_costs.annual_registration_fee,
      };
    }

    // Compute tire_age_months: prefer tire_age_override if set, else derive from model_year
    const oc = draft.operating_costs;
    if (oc.tire_age_override != null && oc.tire_age_override >= 0) {
      // Use explicit override
      savedDraft.operating_costs = {
        ...savedDraft.operating_costs,
        tire_age_months: oc.tire_age_override,
      } as typeof savedDraft.operating_costs;
    } else if (draft.purchase_condition === 'used' && draft.model_year) {
      const currentYear = new Date().getFullYear();
      const vehicleAgeMonths = Math.max(0, (currentYear - draft.model_year) * 12);
      // Derive tire_age_months for non-staggered tires
      const tireInterval = oc.tire_interval_months ?? 24;
      const tireAgeMonths = tireInterval > 0 ? vehicleAgeMonths % tireInterval : 0;
      savedDraft.operating_costs = {
        ...savedDraft.operating_costs,
        tire_age_months: tireAgeMonths,
      } as typeof savedDraft.operating_costs;
    }

    setSaveError(null);
    onSave(savedDraft);
    onClose();
  }

  return (
    // Backdrop — ink-tinted overlay matches the Phase 25 modal pattern in ScenariosPage.
    // data-vehicle-editor-open is a tour signal — the onboarding tour pauses
    // step advancement while this attribute is in the DOM (Phase 25.5.1).
    <div
      role="dialog"
      aria-modal="true"
      data-vehicle-editor-open
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(23, 24, 26, 0.45)', backdropFilter: 'blur(2px)' }}
    >
      {/* Modal */}
      <div
        className="w-full max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto"
        style={{
          background: 'var(--tco-surface)',
          border: '1px solid var(--tco-line)',
          boxShadow: '0 16px 40px rgba(23, 24, 26, 0.12)',
        }}
      >
        {/* Modal header */}
        <div className="px-6 py-4" style={{ borderBottom: '1px solid var(--tco-line)' }}>
          <div className="flex items-center justify-end mb-3">
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg transition-colors"
              style={{ background: 'transparent', border: 'none', color: 'var(--tco-ink-3)', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--tco-line-soft)'; e.currentTarget.style.color = 'var(--tco-ink)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--tco-ink-3)'; }}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <VehicleSelector value={selectorValue} onChange={handleSelectorChange} />

          {/* EPA auto-fill notice */}
          {epaAutoFillVisible && (
            <div
              className="mt-2 rounded-lg px-3 py-2 text-xs"
              style={{
                background: 'var(--tco-accent-soft)',
                border: '1px solid var(--tco-accent)',
                color: 'var(--tco-accent)',
              }}
            >
              Auto-filled from EPA data
            </div>
          )}

          {/* Used-year banner */}
          {selectorValue.year < CURRENT_YEAR - 1 && (
            <div
              className="mt-3 rounded-lg px-4 py-3 text-xs leading-relaxed"
              style={{
                background: 'var(--tco-neg-soft)',
                border: '1px solid var(--tco-neg)',
                color: 'var(--tco-neg)',
              }}
            >
              <strong className="font-semibold">Vehicles from prior model years are treated as used.</strong>{' '}
              Purchase price, residual value, and insurance will need to be entered manually.
            </div>
          )}

          {/* Override label */}
          <div className="mt-3">
            <label
              className="text-xs font-medium block mb-1"
              style={{ color: 'var(--tco-ink-2)' }}
            >
              Override label if needed
            </label>
            <input
              type="text"
              className="text-sm rounded-lg px-3 py-2 focus:outline-none w-full"
              value={selectorValue.label}
              onChange={(e) => {
                const updated = { ...selectorValue, label: e.target.value };
                setSelectorValue(updated);
                handleSelectorChange(updated);
              }}
              placeholder="Override label if needed"
            />
          </div>
        </div>

        {/* Tabs — sticky so tab bar stays visible while scrolling. Active = ink + 2px neg underline,
            mirroring the global Layout.tsx nav pattern. */}
        <div
          className="sticky top-0 z-10 flex gap-0 px-6 overflow-x-auto"
          style={{
            background: 'var(--tco-surface)',
            borderBottom: '1px solid var(--tco-line)',
          }}
        >
          {visibleTabs.map((tab) => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="px-4 py-3 text-sm whitespace-nowrap transition-colors -mb-px"
                style={{
                  background: 'transparent',
                  border: 'none',
                  borderBottom: isActive ? '2px solid var(--tco-neg)' : '2px solid transparent',
                  color: isActive ? 'var(--tco-ink)' : 'var(--tco-ink-2)',
                  fontWeight: isActive ? 600 : 500,
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="px-6 py-5">
          {activeTab === 'purchase' && <PurchaseTab vehicle={draft} onChange={setDraft} horizonMonths={horizonMonths} selectorMsrp={selectorValue.msrp} isCustom={selectorValue.is_custom} hasMsrp={selectorValue.has_msrp} msrpEstimated={selectorValue.msrp_estimated} />}
          {activeTab === 'financing' && <FinancingTab vehicle={draft} onChange={setDraft} />}
          {activeTab === 'operating' && <OperatingTab vehicle={draft} onChange={setDraft} isCustom={selectorValue.is_custom} geoGasPriceLabel={geoGasPriceLabel} hasMpgData={selectorValue.has_mpg_data} />}
          {activeTab === 'operations' && appMode === 'fleet' && (
            <OperationsTab vehicle={draft} onChange={setDraft} vocationMissing={vocationMissing} />
          )}
          {activeTab === 'emotional' && appMode !== 'fleet' && <EmotionalTab vehicle={draft} onChange={setDraft} />}
        </div>

        {/* Footer */}
        <div
          className="flex flex-col gap-2 px-6 py-4"
          style={{ borderTop: '1px solid var(--tco-line)' }}
        >
          {saveError && (
            <p className="text-xs flex items-center gap-1.5" style={{ color: 'var(--tco-neg)' }}>
              <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {saveError}
            </p>
          )}
          {(!draft.operating_costs.annual_insurance_premium || draft.operating_costs.annual_insurance_premium === 0) && (
            <p className="text-xs flex items-center gap-1.5" style={{ color: 'var(--tco-neg)' }}>
              <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Annual insurance is required. Enter your cost in the Operating tab before saving.
            </p>
          )}
          {!selectorValue.is_custom && selectorValue.has_msrp === false && (!draft.depreciation.purchase_price || draft.depreciation.purchase_price === 0) && (
            <p className="text-xs flex items-center gap-1.5" style={{ color: 'var(--tco-neg)' }}>
              <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Purchase price is required. Enter the MSRP from your dealer quote in the Purchase tab.
            </p>
          )}
          {!selectorValue.is_custom && selectorValue.has_mpg_data === false && draft.operating_costs.fuel_type !== 'electric' && (!draft.operating_costs.mpg || draft.operating_costs.mpg === 0) && (
            <p className="text-xs flex items-center gap-1.5" style={{ color: 'var(--tco-neg)' }}>
              <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Fuel economy is required. Enter from manufacturer specs or your fleet's actual consumption.
            </p>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium rounded-lg transition-colors"
              style={{
                background: 'transparent',
                border: '1px solid var(--tco-line)',
                color: 'var(--tco-ink-2)',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--tco-line-soft)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-5 py-2 text-sm font-medium rounded-lg transition-colors"
              style={{
                background: 'var(--tco-ink)',
                border: '1px solid var(--tco-ink)',
                color: 'var(--tco-bg)',
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontWeight: 550,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--tco-ink-2)'; e.currentTarget.style.borderColor = 'var(--tco-ink-2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--tco-ink)'; e.currentTarget.style.borderColor = 'var(--tco-ink)'; }}
            >
              Save Vehicle
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
